import { Component, OnInit, inject } from '@angular/core';
import { PendingFilter } from '../../../models/api/reports/all-filters-response';
import { FilterDetail } from '../../../models/api/reports/filter-detail-response';
import { PendingFilterAction } from '../../../models/api/reports/update-pending-filters';
import { Action } from '../../../models/core/action';
import { CasePipe } from '../../../pipes/case.pipe';
import { FiltersService } from '../../../services/filters/filters.service';
import { ReportsService } from '../../../services/reports/reports.service';
import { Snackbar } from '../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../base/base.component';
import { ActionButtonComponent } from '../../core/buttons/action-button.component';
import { CardComponent } from '../../core/panels/card.component';
import { LoadingPanelComponent } from '../../core/panels/loading-panel.component';
import { MenuComponent } from '../../core/panels/menu.component';
import { ColumnComponent } from '../../core/table/column.component';
import { TableComponent } from '../../core/table/table.component';
import { FilterViewComponent } from '../core/filter-view.component';
import { FormatPipe } from '../../../pipes/format.pipe';

@Component({
  selector: 'app-pending-filters',
  imports: [
    TableComponent,
    ColumnComponent,
    ActionButtonComponent,
    MenuComponent,
    CardComponent,
    FilterViewComponent,
    LoadingPanelComponent,
    CasePipe,
    FormatPipe,
  ],
  template: `
    <app-table
      [dataSource]="pendingFilters"
      class="tw-w-full"
      [expandable]="true"
      [hideExpandAll]="true"
      (rowExpand)="onRowExpand($event)"
      [rowExpandLabel]="rowExpandLabel"
      recordLabel="shared filter"
      scrollHeight=""
      dataKey="filterId"
      [loading]="loading()"
      sortField="shareDate"
      [sortOrder]="-1"
      [showFilterButton]="false"
      [showFilter]="false"
    >
      <app-column
        name="filterName"
        label="Name"
        width="25%"
        classes="tw-font-bold"
        filterType="text"
        ><ng-template let-data
          ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
            {{ data.filterName }}
          </div>
        </ng-template>
      </app-column>
      <app-column
        name="contractNumber"
        label="Client"
        width="25%"
        filterType="select"
        filterLabelName="clientName"
        filterLabelCase="titlecase"
      >
        <ng-template let-data>
          <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
            @if (data.contractNumber && data.clientName) {
              {{ data.contractNumber }} -
              {{ data.clientName | case: 'titlecase' }}
            }
          </div>
        </ng-template>
      </app-column>
      <app-column
        name="sharedBy"
        label="Shared by"
        width="20%"
        filterType="select"
        ><ng-template let-data
          ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
            {{ data.sharedBy }}
          </div>
        </ng-template>
      </app-column>
      <app-column
        name="shareDate"
        label="Received"
        format="dt"
        width="20%"
        filterType="date"
        ><ng-template let-data
          ><div class="tw-max-w-full tw-break-words tw-whitespace-normal">
            {{ data.shareDate | format: 'dt' }}
          </div>
        </ng-template>
      </app-column>
      <app-column
        name="actions"
        label="Actions"
        headerClasses="tw-flex tw-justify-center"
        [frozen]="true"
        [sort]="false"
      >
        <ng-template let-data>
          <div class="tw-max-w-full tw-break-words tw-whitespace-normal">
            <app-action-button
              [menu]="rowMenu"
              [row]="data"
              (click)="rowMenu.toggle($event, data)"
            />
          </div>
        </ng-template>
      </app-column>
      <ng-template #expand let-data>
        <div class="tw-flex tw-gap-4">
          <app-card label="Filter" class="tw-basis-1/2">
            @if (getFilterDetail(data.filterId)) {
              <app-filter-view [dataSource]="getFilterDetail(data.filterId)" />
            } @else {
              <app-loading-panel
                state="Loading"
                loadingStyle="Box"
                [loadingHeight]="200"
              />
            }
          </app-card>
        </div>
      </ng-template>
    </app-table>
    <app-menu
      [items]="rowActions"
      #rowMenu
      (click)="onAction($event, rowMenu.data)"
    />
    <div class="tw-pt-8 tw-italic tw-text-sm">
      Shared filters are filters that have been shared with you by another user.
      Click Accept to save shared filters to your Filters tab.
    </div>
  `,
  styles: [],
})
export class PendingFiltersComponent extends BaseComponent implements OnInit {
  private readonly snackbar = inject(Snackbar);
  private readonly filtersService = inject(FiltersService);
  private readonly reportsService = inject(ReportsService);

  pendingFilters: PendingFilter[] = [];
  filterDetails: Record<string, FilterDetail> = {};

  public rowExpandLabel = (filter: PendingFilter): string => {
    return `${filter.filterName} shared filter`;
  };

  readonly loading = this.filtersService.pendingFiltersLoading;

  readonly rowActions: Action[] = [
    { label: 'Accept', key: 'accept' },
    { label: 'Decline', key: 'decline' },
  ];

  async ngOnInit() {
    this.dispose.subs = this.reportsService.reportsLoaded.subscribe(
      (result) => {
        this.pendingFilters = result.pendingFiltersResponse.pendingFilters;
      },
    );
    await this.loadPendingFilters();
  }

  private async loadPendingFilters() {
    await this.filtersService.loadPendingFilters('JURIS', 'CLAIM', null);
    this.pendingFilters = this.filtersService.pendingFilters();
  }

  async onRowExpand(event: { data: PendingFilter }) {
    const filter = event.data;
    if (!this.filterDetails[filter.filterId]) {
      await this.loadFilterDetail(filter.filterId, filter.contractNumber);
      this.filterDetails[filter.filterId].filterName = filter.filterName;
    }
  }

  private async loadFilterDetail(filterId: string, contractNumber: string) {
    const filterDetail = await this.filtersService.getFilterDetail(
      filterId,
      contractNumber,
    );
    this.filterDetails[filterId] = filterDetail;
  }

  getFilterDetail(filterId: string): FilterDetail {
    return this.filterDetails[filterId];
  }

  onAction(action: Action, filter: PendingFilter) {
    if (action.key === 'decline') {
      this.confirm.show({
        label: 'Decline shared filter',
        message: `Are you sure you want to decline "${filter.filterName}"?`,
        width: '35rem',
        data: filter,
        confirm: (data) => this.deleteFilter(data),
      });
    } else if (action.key === 'accept') {
      this.acceptFilter(filter);
    }
  }

  deleteFilter(filter: PendingFilter) {
    this.updatePendingFilter(filter, 'Delete');
  }

  acceptFilter(filter: PendingFilter) {
    this.updatePendingFilter(filter, 'Accept');
  }

  private async updatePendingFilter(
    filter: PendingFilter,
    action: PendingFilterAction,
  ) {
    const response = await this.filtersService.updatePendingFilter(
      filter.filterId,
      action,
      'JURIS',
    );
    this.pendingFilters = this.filtersService.pendingFilters();

    const actionText = action === 'Accept' ? 'accepted' : 'declined';
    this.snackbar.show(
      `Shared filter ${actionText}`,
      'Success',
      'fa-light fa-file-lines',
      3000,
    );

    if (response.emails && response.emails.length > 0) {
      const emailText = response.emails.length === 1 ? 'email' : 'emails';
      this.snackbar.show(
        `Notification ${emailText} sent`,
        'Info',
        'fa-light fa-envelope',
        2000,
      );
    }
  }
}
